import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import Chart from 'chart.js/auto';
import {
  Badge,
  Button,
  Card,
  Form,
  Spinner,
} from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import { hasPermission, PERMISSION_IDS } from '../../utils/permissions';
import '../../styles/admin.css';
import './AdminDashboard.css';
import useAdminGuard from '../../hooks/useAdminGuard';
import { formatARS } from '../../utils/currency';

const PERIOD_OPTIONS = [
  { value: 'day', label: 'Día' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mes' },
  { value: 'year', label: 'Año' },
];

const PLACEHOLDER_IMG =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="52" height="52"><rect width="52" height="52" rx="12" ry="12" fill="%23e5e7eb"/></svg>';

/** Debe coincidir con ADMIN_TEST_PAYMENT_CART_ID en el servidor (checkout). */
const ADMIN_TEST_PAYMENT_CART_ID = 'admin-test-payment-custom';
const ADMIN_TEST_PAYMENT_PRICE_ARS = 1000;

const parseMaybeJSON = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'string') return [];
  try {
    return JSON.parse(raw);
  } catch {
    return [];
  }
};

const resolveImageUrl = (source) => {
  if (!source) return PLACEHOLDER_IMG;
  const base = getApiBaseUrl();
  const normalised = String(source).replace(/\\/g, '/').replace(/["'<>]/g, '');
  if (/^https?:\/\//i.test(normalised)) return normalised;
  if (normalised.startsWith('/')) return `${base}${normalised}`;
  if (normalised.startsWith('uploads/')) return `${base}/${normalised}`;
  if (normalised.startsWith('items/images/')) return `${base}/uploads/${normalised}`;
  if (normalised.startsWith('heroes/')) return `${base}/uploads/${normalised}`;
  return `${base}/uploads/items/images/${normalised}`;
};

/** Slides persistidos o legado img/video (misma lógica que en HeroSection). */
const parseHeroSlides = (hero) => {
  if (!hero) return [];
  let raw = hero.slides;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = null;
    }
  }
  if (Array.isArray(raw) && raw.length > 0) {
    return raw
      .filter((s) => s && (s.type === 'image' || s.type === 'video') && s.url)
      .map((s) => ({ type: s.type, url: s.url }));
  }
  const mediaType = hero.media_type || 'image';
  if (mediaType === 'video' && hero.video) return [{ type: 'video', url: hero.video }];
  if (hero.img) return [{ type: 'image', url: hero.img }];
  return [];
};

const newHeroSlideRow = () => ({
  key: `slide-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  type: 'image',
  url: '',
  file: null,
  preview: null,
});

const formatChartTickLabel = (text, maxLen = 26) => {
  const s = String(text || '').trim();
  if (s.length <= maxLen) return s;
  return `${s.slice(0, Math.max(0, maxLen - 1))}…`;
};

const fitCategoryAxisWidth = (axis) => {
  const chart = axis.chart;
  const labels = chart.data.labels || [];
  const tickFont = axis.options.ticks?.font;
  const size = typeof tickFont === 'object' && tickFont?.size ? tickFont.size : 10;
  const ctx = chart.ctx;
  ctx.save();
  ctx.font = `600 ${size}px system-ui, -apple-system, "Segoe UI", sans-serif`;
  const widest = labels.reduce((max, label) => {
    const w = ctx.measureText(String(label)).width;
    return Math.max(max, w);
  }, 0);
  ctx.restore();
  axis.width = Math.min(Math.ceil(widest) + 16, chart.width * 0.48);
};

const createHorizontalBarOptions = (fullLabels) => ({
  indexAxis: 'y',
  responsive: true,
  maintainAspectRatio: false,
  layout: { padding: { left: 4, right: 10, top: 6, bottom: 6 } },
  animation: {
    duration: 650,
    easing: 'easeOutQuart',
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: 'rgba(15, 23, 42, 0.92)',
      padding: 12,
      cornerRadius: 10,
      titleFont: { size: 13, weight: '600' },
      bodyFont: { size: 13 },
      displayColors: false,
      callbacks: {
        title(tooltipItems) {
          const idx = tooltipItems[0]?.dataIndex;
          return fullLabels[idx] ?? tooltipItems[0]?.label ?? '';
        },
      },
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      grid: { color: 'rgba(148, 163, 184, 0.22)', drawBorder: false },
      ticks: { font: { size: 10 }, color: '#64748b', maxTicksLimit: 6 },
    },
    y: {
      grid: { display: false, drawBorder: false },
      ticks: {
        autoSkip: false,
        font: { size: 10 },
        color: '#334155',
        padding: 6,
      },
      afterFit: fitCategoryAxisWidth,
    },
  },
});

/** Chart.js + ResizeObserver: evita el error de Chrome si resize/update coinciden con el layout. */
const scheduleChartResize = (chart) => {
  if (!chart) return;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      try {
        chart.resize();
      } catch {
        /* instancia ya destruida */
      }
    });
  });
};

const ChartSection = ({ title, titleIcon, canvasId, chartRef, isEmpty, emptyMessage, barTone = 'blue' }) => {
  const toneClass = barTone === 'green' ? 'AD-chart-card--green' : 'AD-chart-card--blue';
  return (
    <Card className={`AD-card AD-chart-card ${toneClass} h-100`}>
      <Card.Header className="AD-card-header">
        <h3 className="AD-card-title">
          {titleIcon ? <i className={`bi ${titleIcon} AD-card-title-icon`} aria-hidden /> : null}
          {title}
        </h3>
      </Card.Header>
      <Card.Body className="AD-card-body">
        <div className="AD-chart-wrapper">
          {isEmpty ? (
            <div className="AD-chart-placeholder">
              <div className="AD-chart-placeholder-icon" aria-hidden>
                <i className="bi bi-bar-chart-line" />
              </div>
              <p className="AD-chart-placeholder-text">{emptyMessage}</p>
            </div>
          ) : (
            <canvas key={canvasId} ref={chartRef} id={canvasId} />
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

const AdminDashboardInner = () => {
  const { user, token } = useAdminGuard();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { notify } = useAdminNotifications();
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    buyers: 0,
  });
  const [topItems, setTopItems] = useState([]);
  const [topCourses, setTopCourses] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [heroes, setHeroes] = useState([]);
  const [heroesLoading, setHeroesLoading] = useState(false);
  const [isHeroesCollapsed, setIsHeroesCollapsed] = useState(false);
  const [showHeroModal, setShowHeroModal] = useState(false);
  const [editingHero, setEditingHero] = useState(null);
  const [heroForm, setHeroForm] = useState({ textbuttom: '', urlbuttom: '' });
  const [heroSlideRows, setHeroSlideRows] = useState(() => [newHeroSlideRow()]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const itemsChartRef = useRef(null);
  const coursesChartRef = useRef(null);
  const charts = useRef({ items: null, courses: null });
  const dashboardGridRef = useRef(null);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token],
  );

  const fetchJson = useCallback(
    async (endpoint) => {
      const response = await fetch(buildApiUrl(endpoint), {
        headers: authHeaders,
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error ${response.status}: ${errorText || response.statusText}`);
      }
      return response.json();
    },
    [authHeaders],
  );

  // Verificar si el usuario tiene el permiso para ver estadísticas (PANEL_ADMIN: 741258)
  const hasStatsPermission = useMemo(() => {
    if (!user) return false;
    return hasPermission(user, PERMISSION_IDS.PANEL_ADMIN);
  }, [user]);

  // Verificar si el usuario tiene el permiso Editor Principal
  const hasEditorPrincipalPermission = useMemo(() => {
    if (!user) return false;
    return hasPermission(user, PERMISSION_IDS.EDITOR_PRINCIPAL);
  }, [user]);

  const loadData = useCallback(async () => {
    if (!token || !hasStatsPermission) return;
    setLoading(true);

    try {
      const [itemsResponse, coursesResponse, recentResponse, usersResponse] = await Promise.all([
        fetchJson(`/api/dashboard/stats/top-items?period=${period}&limit=8`),
        fetchJson(`/api/dashboard/stats/top-courses?period=${period}&limit=8`),
        fetchJson('/api/dashboard/stats/recent-item-orders?limit=8'),
        fetchJson('/api/user/list'),
      ]);

      setTopItems(itemsResponse.items || []);
      setTopCourses(coursesResponse.courses || []);
      const orders = (recentResponse.orders || []).map((order) => {
        const images = parseMaybeJSON(order.ImagenesItems);
        const imageUrl = resolveImageUrl(images[0]);
        return {
          id: order.orderId,
          name: order.itemName,
          quantity: order.quantity,
          unitPrice: Number(order.unitPrice || 0),
          createdAt: order.createdAt,
          imageUrl,
        };
      });
      setRecentOrders(orders);

      const totalUsers = usersResponse.users?.length || 0;
      // Contar usuarios con compras basándose en órdenes reales
      const buyers = (usersResponse.users || []).filter((user) => {
        // Verificar si tiene compras (campo compras o hasPurchases o totalOrders)
        if (user.hasPurchases) return true;
        if (user.totalOrders && user.totalOrders > 0) return true;
        if (user.compras) {
          if (Array.isArray(user.compras)) return user.compras.length > 0;
          try {
            const purchases = typeof user.compras === 'string' ? JSON.parse(user.compras) : user.compras;
            return Array.isArray(purchases) && purchases.length > 0;
          } catch {
            return Boolean(user.compras);
          }
        }
        return false;
      }).length;

      setStats({ totalUsers, buyers });
    } catch (err) {
      console.error('[AdminDashboard] Error loading data', err);
      notify({
        title: 'Error al cargar datos',
        message: 'Ocurrió un problema obteniendo la información del dashboard.',
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [fetchJson, notify, period, token, hasStatsPermission]);

  useEffect(() => {
    if (hasStatsPermission) {
      loadData();
    }
  }, [loadData, hasStatsPermission]);

  // Cargar heroes si tiene permiso
  const loadHeroes = useCallback(async () => {
    if (!token || !hasEditorPrincipalPermission) return;
    setHeroesLoading(true);
    try {
      const response = await fetchJson('/api/heroes/admin');
      setHeroes(response.heroes || []);
    } catch (err) {
      console.error('[AdminDashboard] Error loading heroes', err);
      notify({
        title: 'Error al cargar heroes',
        message: 'Ocurrió un problema obteniendo los heroes.',
        variant: 'error',
      });
    } finally {
      setHeroesLoading(false);
    }
  }, [fetchJson, notify, token, hasEditorPrincipalPermission]);

  useEffect(() => {
    if (hasEditorPrincipalPermission) {
      loadHeroes();
    }
  }, [loadHeroes, hasEditorPrincipalPermission]);

  // Función para subir imagen
  const handleImageUpload = async (file) => {
    if (!file) return null;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('heroImage', file);
      
      const response = await fetch(buildApiUrl('/api/heroes/upload-image'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al subir imagen');
      }
      
      const data = await response.json();
      return data.url;
    } catch (err) {
      console.error('[AdminDashboard] Error uploading image', err);
      notify({
        title: 'Error al subir imagen',
        message: err.message || 'Ocurrió un problema al subir la imagen.',
        variant: 'error',
      });
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  // Función para subir video
  const handleVideoUpload = async (file) => {
    if (!file) return null;
    
    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('heroVideo', file);
      
      let response;
      try {
        response = await fetch(buildApiUrl('/api/heroes/upload-video'), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });
      } catch (networkErr) {
        throw new Error(
          'No se pudo conectar con la API. Si ves error de CORS y 413, el proxy (Nginx) está rechazando el archivo por tamaño. ' +
          'En el servidor donde está la API, configura client_max_body_size 250M en Nginx para el location que hace proxy a la API.'
        );
      }
      
      if (!response.ok) {
        let message = 'Error al subir video';
        const isOpaqueOrNoCors = response.type === 'opaque' || response.status === 0;
        if (isOpaqueOrNoCors) {
          message = 'El servidor rechazó el archivo por tamaño (413) o no envió CORS. En el servidor de api.alwaysaligners.com, en el bloque server { ... } que tiene server_name api.alwaysaligners.com, añade: client_max_body_size 250M; y ejecuta: sudo nginx -t && sudo systemctl reload nginx';
        } else if (response.status === 413) {
          message = 'El video es demasiado grande o Nginx rechaza el tamaño. En el servidor de la API (api.alwaysaligners.com), en el bloque de ese dominio, añade client_max_body_size 250M; y reinicia Nginx.';
        } else {
          try {
            const errorData = await response.json();
            message = errorData.error || errorData.message || message;
          } catch (_) {}
        }
        throw new Error(message);
      }
      
      const data = await response.json();
      return data.url;
    } catch (err) {
      console.error('[AdminDashboard] Error uploading video', err);
      notify({
        title: 'Error al subir video',
        message: err.message || 'Ocurrió un problema al subir el video.',
        variant: 'error',
      });
      return null;
    } finally {
      setUploadingVideo(false);
    }
  };

  const resolveSlideRowsToPayload = async (rows) => {
    const out = [];
    for (const row of rows) {
      let url = String(row.url || '').trim();
      if (row.file) {
        if (row.type === 'video') {
          const uploaded = await handleVideoUpload(row.file);
          if (!uploaded) return null;
          url = uploaded;
        } else {
          const uploaded = await handleImageUpload(row.file);
          if (!uploaded) return null;
          url = uploaded;
        }
      }
      if (url) out.push({ type: row.type, url });
    }
    return out;
  };

  const handleHeroSlideFileChange = (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const row = heroSlideRows[index];
    if (row.type === 'video' && !String(file.type || '').startsWith('video/')) {
      notify({
        title: 'Archivo inválido',
        message: 'Seleccioná un archivo de video.',
        variant: 'warning',
      });
      return;
    }
    if (row.type === 'image' && !String(file.type || '').startsWith('image/')) {
      notify({
        title: 'Archivo inválido',
        message: 'Seleccioná una imagen.',
        variant: 'warning',
      });
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setHeroSlideRows((rows) =>
        rows.map((r, i) => (i === index ? { ...r, file, preview: reader.result } : r)),
      );
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleHeroSlideTypeChange = (index, type) => {
    setHeroSlideRows((rows) =>
      rows.map((r, i) => (i === index ? { ...r, type, file: null, preview: null } : r)),
    );
  };

  const handleHeroSlideRemove = (index) => {
    setHeroSlideRows((rows) => (rows.length <= 1 ? rows : rows.filter((_, i) => i !== index)));
  };

  const addHeroSlideRow = () => {
    setHeroSlideRows((rows) => [...rows, newHeroSlideRow()]);
  };

  const moveHeroSlide = (index, delta) => {
    setHeroSlideRows((rows) => {
      const j = index + delta;
      if (j < 0 || j >= rows.length) return rows;
      const copy = [...rows];
      const tmp = copy[index];
      copy[index] = copy[j];
      copy[j] = tmp;
      return copy;
    });
  };

  // Funciones para gestionar heroes
  const handleCreateHero = async () => {
    if (!heroForm.textbuttom || !heroForm.urlbuttom) {
      notify({
        title: 'Campos requeridos',
        message: 'El texto del botón y la URL son obligatorios.',
        variant: 'warning',
      });
      return;
    }
    try {
      setHeroesLoading(true);

      const slidesPayload = await resolveSlideRowsToPayload(heroSlideRows);
      if (slidesPayload === null) {
        setHeroesLoading(false);
        return;
      }
      if (!slidesPayload.length) {
        notify({
          title: 'Medios requeridos',
          message: 'Agregá al menos un slide con imagen o video (archivo o URL ya guardada).',
          variant: 'warning',
        });
        setHeroesLoading(false);
        return;
      }

      const response = await fetch(buildApiUrl('/api/heroes'), {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          textbuttom: heroForm.textbuttom,
          urlbuttom: heroForm.urlbuttom,
          slides: slidesPayload,
        }),
      });
      if (!response.ok) throw new Error('Error al crear hero');
      await loadHeroes();
      setShowHeroModal(false);
      setHeroForm({ textbuttom: '', urlbuttom: '' });
      setHeroSlideRows([newHeroSlideRow()]);
      notify({
        title: 'Hero creado',
        message: 'El hero se ha creado exitosamente.',
        variant: 'success',
      });
    } catch (err) {
      console.error('[AdminDashboard] Error creating hero', err);
      notify({
        title: 'Error al crear hero',
        message: 'Ocurrió un problema al crear el hero.',
        variant: 'error',
      });
    } finally {
      setHeroesLoading(false);
    }
  };

  const handleUpdateHero = async () => {
    if (!editingHero || !heroForm.textbuttom || !heroForm.urlbuttom) {
      notify({
        title: 'Campos requeridos',
        message: 'El texto del botón y la URL son obligatorios.',
        variant: 'warning',
      });
      return;
    }
    try {
      setHeroesLoading(true);

      const slidesPayload = await resolveSlideRowsToPayload(heroSlideRows);
      if (slidesPayload === null) {
        setHeroesLoading(false);
        return;
      }
      if (!slidesPayload.length) {
        notify({
          title: 'Medios requeridos',
          message: 'Debe haber al menos un slide con imagen o video.',
          variant: 'warning',
        });
        setHeroesLoading(false);
        return;
      }

      const response = await fetch(buildApiUrl(`/api/heroes/${editingHero.id}`), {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          textbuttom: heroForm.textbuttom,
          urlbuttom: heroForm.urlbuttom,
          slides: slidesPayload,
        }),
      });
      if (!response.ok) throw new Error('Error al actualizar hero');
      await loadHeroes();
      setShowHeroModal(false);
      setEditingHero(null);
      setHeroForm({ textbuttom: '', urlbuttom: '' });
      setHeroSlideRows([newHeroSlideRow()]);
      notify({
        title: 'Hero actualizado',
        message: 'El hero se ha actualizado exitosamente.',
        variant: 'success',
      });
    } catch (err) {
      console.error('[AdminDashboard] Error updating hero', err);
      notify({
        title: 'Error al actualizar hero',
        message: 'Ocurrió un problema al actualizar el hero.',
        variant: 'error',
      });
    } finally {
      setHeroesLoading(false);
    }
  };

  const handleDeleteHero = async (heroId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este hero?')) return;
    try {
      setHeroesLoading(true);
      const response = await fetch(buildApiUrl(`/api/heroes/${heroId}`), {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (!response.ok) throw new Error('Error al eliminar hero');
      await loadHeroes();
      notify({
        title: 'Hero eliminado',
        message: 'El hero se ha eliminado exitosamente.',
        variant: 'success',
      });
    } catch (err) {
      console.error('[AdminDashboard] Error deleting hero', err);
      notify({
        title: 'Error al eliminar hero',
        message: 'Ocurrió un problema al eliminar el hero.',
        variant: 'error',
      });
    } finally {
      setHeroesLoading(false);
    }
  };

  const openEditModal = (hero) => {
    setEditingHero(hero);
    setHeroForm({
      textbuttom: hero.textbuttom || '',
      urlbuttom: hero.urlbuttom || '',
    });
    const parsed = parseHeroSlides(hero);
    setHeroSlideRows(
      parsed.length
        ? parsed.map((s) => ({
            key: `slide-${Math.random().toString(36).slice(2, 11)}`,
            type: s.type,
            url: s.url,
            file: null,
            preview: null,
          }))
        : [newHeroSlideRow()],
    );
    setShowHeroModal(true);
  };

  const openCreateModal = () => {
    setEditingHero(null);
    setHeroForm({ textbuttom: '', urlbuttom: '' });
    setHeroSlideRows([newHeroSlideRow()]);
    setShowHeroModal(true);
  };

  const closeHeroModal = () => {
    setShowHeroModal(false);
    setEditingHero(null);
    setHeroForm({ textbuttom: '', urlbuttom: '' });
    setHeroSlideRows([newHeroSlideRow()]);
  };

  // Limpiar gráficos cuando el componente se desmonte
  useEffect(() => {
    return () => {
      if (charts.current.items) {
        charts.current.items.destroy();
        charts.current.items = null;
      }
      if (charts.current.courses) {
        charts.current.courses.destroy();
        charts.current.courses = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!itemsChartRef.current) return undefined;

    // Si no hay datos, destruir el gráfico existente
    if (!topItems.length) {
      if (charts.current.items) {
        charts.current.items.destroy();
        charts.current.items = null;
      }
      return undefined;
    }

    const fullLabels = topItems.map((item) => item.name);
    const labels = fullLabels.map((name) => formatChartTickLabel(name));
    const data = topItems.map((item) => item.totalQty);

    // Si ya existe un gráfico, actualizarlo en lugar de recrearlo
    if (charts.current.items) {
      charts.current.items.data.labels = labels;
      charts.current.items.data.datasets[0].data = data;
      charts.current.items.update('none');
      scheduleChartResize(charts.current.items);
      return undefined;
    }

    // Crear nuevo gráfico solo si no existe uno
    const chartInstance = new Chart(itemsChartRef.current, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Unidades vendidas',
            data,
            borderRadius: 8,
            maxBarThickness: 22,
            backgroundColor: 'rgba(59, 130, 246, 0.78)',
            hoverBackgroundColor: 'rgba(37, 99, 235, 0.92)',
          },
        ],
      },
      options: createHorizontalBarOptions(fullLabels),
    });
    charts.current.items = chartInstance;

    return () => {
      if (charts.current.items) {
        charts.current.items.destroy();
        charts.current.items = null;
      }
    };
  }, [topItems]);

  useEffect(() => {
    if (!coursesChartRef.current) return undefined;

    // Si no hay datos, destruir el gráfico existente
    if (!topCourses.length) {
      if (charts.current.courses) {
        charts.current.courses.destroy();
        charts.current.courses = null;
      }
      return undefined;
    }

    const fullLabels = topCourses.map((course) => course.name);
    const labels = fullLabels.map((name) => formatChartTickLabel(name));
    const data = topCourses.map((course) => course.totalQty);

    // Si ya existe un gráfico, actualizarlo en lugar de recrearlo
    if (charts.current.courses) {
      charts.current.courses.data.labels = labels;
      charts.current.courses.data.datasets[0].data = data;
      charts.current.courses.update('none');
      scheduleChartResize(charts.current.courses);
      return undefined;
    }

    // Crear nuevo gráfico solo si no existe uno
    const chartInstance = new Chart(coursesChartRef.current, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Cursos vendidos',
            data,
            borderRadius: 8,
            maxBarThickness: 22,
            backgroundColor: 'rgba(34, 197, 94, 0.78)',
            hoverBackgroundColor: 'rgba(22, 163, 74, 0.92)',
          },
        ],
      },
      options: createHorizontalBarOptions(fullLabels),
    });
    charts.current.courses = chartInstance;

    return () => {
      if (charts.current.courses) {
        charts.current.courses.destroy();
        charts.current.courses = null;
      }
    };
  }, [topCourses]);

  useEffect(() => {
    const gridEl = dashboardGridRef.current;
    if (!gridEl) return undefined;

    const resizeCharts = () => {
      scheduleChartResize(charts.current.items);
      scheduleChartResize(charts.current.courses);
    };

    const observer = new ResizeObserver(resizeCharts);
    observer.observe(gridEl);
    window.addEventListener('resize', resizeCharts);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', resizeCharts);
    };
  }, [loading, topItems.length, topCourses.length]);

  const handlePeriodChange = (event) => {
    setPeriod(event.target.value);
  };

  const handleTestPaymentClick = useCallback(async () => {
    const ok = await addToCart(
      {
        id: ADMIN_TEST_PAYMENT_CART_ID,
        name: `Producto Custom — Test de pago (${formatARS(ADMIN_TEST_PAYMENT_PRICE_ARS)})`,
        price: ADMIN_TEST_PAYMENT_PRICE_ARS,
        image: '',
        productType: 'custom',
        quantity: 1,
      },
      token,
    );
    if (ok) {
      notify({
        title: 'Producto de prueba agregado',
        message: 'Te llevamos al carrito para que puedas probar el flujo de pago.',
        variant: 'success',
      });
      navigate('/cart');
    }
  }, [addToCart, token, notify, navigate]);

  if (!user) {
    return null;
  }

  const userName = user.name || user.nombre || 'Usuario';

  return (
    <div className="admin-dashboard">
      <div className="admin-layout">
        <AdminSidebar
          activeKey="dashboard"
          onNavigate={(item) => {
            if (item.path === '/admin' || item.path === '/admin/dashboard' || item.eventKey === 'dashboard') {
              navigate('/admin');
              return;
            }
            if (item.path) {
              navigate(item.path);
              return;
            }
            notify({
              title: 'Sección en desarrollo',
              message: `La sección "${item.label}" estará disponible muy pronto.`,
              variant: 'info',
            });
          }}
        />
        <main className="admin-main">
          <div className="AD-container">
            {!hasStatsPermission ? (
              // Mostrar mensaje de bienvenida si no tiene permiso para ver estadísticas
              <div className="AD-welcome-container">
                <Card className="AD-welcome-card">
                  <Card.Body className="AD-welcome-body">
                    <div className="AD-welcome-icon">
                      <i className="bi-person-circle" />
                    </div>
                    <h1 className="AD-welcome-title">Bienvenido {userName}</h1>
                    <p className="AD-welcome-subtitle">Al panel administrativo</p>
                    <Button
                      type="button"
                      variant="outline-secondary"
                      className="mt-3"
                      onClick={handleTestPaymentClick}
                    >
                      <i className="bi-credit-card me-2" aria-hidden />
                      Test de pago
                    </Button>
                  </Card.Body>
                </Card>
              </div>
            ) : (
              // Mostrar estadísticas si tiene el permiso
              <>
            <div className="AD-header">
              <div className="AD-header-content">
                <p className="AD-header-eyebrow">Resumen general</p>
                <h1 className="AD-title">Panel administrativo</h1>
                <p className="AD-subtitle">
                  Hola de nuevo,{' '}
                  <span className="AD-user-name">{userName}</span>.
                  Visualizá métricas clave y el rendimiento del ecosistema Always Aligners.
                </p>
              </div>
              <div className="AD-controls">
                <Form.Select
                  value={period}
                  onChange={handlePeriodChange}
                  className="AD-period-select"
                  aria-label="Periodo de las métricas"
                >
                  {PERIOD_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <Button
                  variant="primary"
                  onClick={loadData}
                  disabled={loading}
                  className="AD-refresh-button"
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-2" />
                      Actualizando
                    </>
                  ) : (
                    <>
                      <i className="bi-arrow-repeat me-2" />
                      Actualizar
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={handleTestPaymentClick}
                  className="AD-test-payment-button"
                  title="Agrega un producto Custom de $1000 ARS al carrito para probar la compra"
                >
                  <i className="bi-credit-card me-2" aria-hidden />
                  Test de pago
                </Button>
              </div>
            </div>

            <section ref={dashboardGridRef} className="AD-dashboard-grid" aria-label="Métricas y resumen">
              <div className="AD-dashboard-grid__stat AD-stat-card AD-stat-card-primary">
                <div className="AD-stat-content">
                  <div className="AD-stat-icon AD-stat-icon-primary">
                    <i className="bi-people-fill" />
                  </div>
                  <div className="AD-stat-info">
                    <div className="AD-stat-value">{stats.totalUsers}</div>
                    <div className="AD-stat-label">Usuarios registrados</div>
                  </div>
                </div>
              </div>
              <div className="AD-dashboard-grid__stat AD-stat-card AD-stat-card-success">
                <div className="AD-stat-content">
                  <div className="AD-stat-icon AD-stat-icon-success">
                    <i className="bi-bag-check-fill" />
                  </div>
                  <div className="AD-stat-info">
                    <div className="AD-stat-value">{stats.buyers}</div>
                    <div className="AD-stat-label">Usuarios con compras</div>
                  </div>
                </div>
              </div>
              <div className="AD-dashboard-grid__stat AD-stat-card AD-stat-card-warning">
                <div className="AD-stat-content">
                  <div className="AD-stat-icon AD-stat-icon-warning">
                    <i className="bi-stack" />
                  </div>
                  <div className="AD-stat-info">
                    <div className="AD-stat-value">{topItems.length}</div>
                    <div className="AD-stat-label">Items analizados</div>
                  </div>
                </div>
              </div>
              <div className="AD-dashboard-grid__stat AD-stat-card AD-stat-card-info">
                <div className="AD-stat-content">
                  <div className="AD-stat-icon AD-stat-icon-info">
                    <i className="bi-bar-chart-line" />
                  </div>
                  <div className="AD-stat-info">
                    <div className="AD-stat-value">{topCourses.length}</div>
                    <div className="AD-stat-label">Cursos analizados</div>
                  </div>
                </div>
              </div>

              <div className="AD-dashboard-grid__panel AD-dashboard-grid__panel--chart">
                <ChartSection
                  title="Items más vendidos"
                  titleIcon="bi-box-seam"
                  canvasId="chartItems"
                  chartRef={itemsChartRef}
                  isEmpty={topItems.length === 0}
                  emptyMessage="No hay ventas de items en este periodo. Probá otro rango de fechas o esperá a que se registren pedidos."
                  barTone="blue"
                />
              </div>
              <div className="AD-dashboard-grid__panel AD-dashboard-grid__panel--chart">
                <ChartSection
                  title="Cursos más vendidos"
                  titleIcon="bi-mortarboard"
                  canvasId="chartCourses"
                  chartRef={coursesChartRef}
                  isEmpty={topCourses.length === 0}
                  emptyMessage="No hay cursos con ventas en este periodo. Cambiá el filtro de periodo para comparar."
                  barTone="green"
                />
              </div>
              <div className="AD-dashboard-grid__panel AD-dashboard-grid__panel--orders">
              <Card className="AD-card AD-side-card AD-recent-orders-card h-100">
                <Card.Header className="AD-card-header">
                  <h3 className="AD-card-title">
                    <i className="bi bi-receipt AD-card-title-icon" aria-hidden />
                    Últimos pedidos
                  </h3>
                  <Badge bg="primary" pill className="AD-card-badge">
                    {recentOrders.length}
                  </Badge>
                </Card.Header>
                <Card.Body className="AD-card-body">
                  {loading && (
                    <div className="d-flex justify-content-center py-3">
                      <Spinner animation="border" size="sm" />
                    </div>
                  )}
                  {!loading && recentOrders.length === 0 && (
                    <div className="AD-empty-state">
                      <i className="bi-inbox" />
                      <p>No hay pedidos recientes en el periodo seleccionado.</p>
                    </div>
                  )}
                  {!loading && recentOrders.length > 0 && (
                    <div className="AD-orders-table-wrap">
                      <table className="AD-orders-table">
                        <thead>
                          <tr>
                            <th scope="col" className="AD-orders-table__col-thumb" />
                            <th scope="col">Producto</th>
                            <th scope="col" className="AD-orders-table__col-qty">Cant.</th>
                            <th scope="col" className="AD-orders-table__col-price">Precio</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentOrders.slice(0, 5).map((order, orderIdx) => (
                            <tr
                              key={`recent-${order.id}-${order.createdAt ?? ''}-${order.name ?? ''}-${orderIdx}`}
                            >
                              <td className="AD-orders-table__col-thumb">
                                <img src={order.imageUrl} alt="" />
                              </td>
                              <td className="AD-orders-table__name" title={order.name}>
                                {order.name}
                              </td>
                              <td className="AD-orders-table__col-qty">×{order.quantity}</td>
                              <td className="AD-orders-table__col-price">{formatARS(order.unitPrice)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card.Body>
              </Card>
              </div>
            </section>

            {/* Sección de Heroes - Solo visible con permiso EditorPrincipal */}
            {hasEditorPrincipalPermission && (
              <div className="AD-heroes-section">
                <div className="AD-heroes-card">
                  <div 
                    className="AD-heroes-header"
                    onClick={() => setIsHeroesCollapsed(!isHeroesCollapsed)}
                  >
                    <div className="AD-heroes-header-content">
                      <i className={`bi-chevron-${isHeroesCollapsed ? 'right' : 'down'} AD-heroes-chevron`} />
                      <h3 className="AD-heroes-title">
                        <i className="bi-image AD-heroes-icon" />
                        Gestión de Heroes
                      </h3>
                    </div>
                    <div className="AD-heroes-badge">
                      {heroes.length}
                    </div>
                  </div>
                  
                  {!isHeroesCollapsed && (
                    <div className="AD-heroes-body">
                      <div className="AD-heroes-actions">
                        <button
                          className="AD-heroes-create-btn"
                          onClick={openCreateModal}
                          disabled={heroesLoading}
                        >
                          <i className="bi-plus-circle" />
                          Crear Hero
                        </button>
                      </div>
                      <p className="small text-muted mb-3 px-1" style={{ maxWidth: '52rem' }}>
                        En la página principal la secuencia une los medios de <strong>todos</strong> los heroes
                        (el más reciente primero). También podés cargar varios slides dentro de un solo hero.
                      </p>
                      
                      {heroesLoading && (
                        <div className="AD-heroes-loading">
                          <Spinner animation="border" />
                        </div>
                      )}
                      
                      {!heroesLoading && heroes.length === 0 && (
                        <div className="AD-heroes-empty">
                          <i className="bi-image" />
                          <p>No hay heroes creados. Crea uno nuevo para comenzar.</p>
                        </div>
                      )}
                      
                      {!heroesLoading && heroes.length > 0 && (
                        <div className="AD-heroes-list">
                          {heroes.map((hero) => {
                            const heroSlides = parseHeroSlides(hero);
                            const firstSlide = heroSlides[0];
                            return (
                            <div key={hero.id} className="AD-hero-item">
                              {firstSlide ? (
                                <div className="AD-hero-image-wrapper">
                                  {firstSlide.type === 'video' ? (
                                    <video
                                      src={resolveImageUrl(firstSlide.url)}
                                      className="AD-hero-image"
                                      style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                                      muted
                                    />
                                  ) : (
                                    <img
                                      src={resolveImageUrl(firstSlide.url)}
                                      alt="Hero"
                                      className="AD-hero-image"
                                    />
                                  )}
                                  <div className="AD-hero-media-badge">
                                    <i className={`bi ${firstSlide.type === 'video' ? 'bi-play-circle' : 'bi-image'}`} />
                                    {firstSlide.type === 'video' ? 'Video' : 'Imagen'}
                                    {heroSlides.length > 1 ? ` · ${heroSlides.length} en secuencia` : ''}
                                  </div>
                                </div>
                              ) : null}
                              <div className="AD-hero-content">
                                <div className="AD-hero-info">
                                  <div className="AD-hero-field">
                                    <strong className="AD-hero-label">Secuencia:</strong>
                                    <p className="AD-hero-value">
                                      {heroSlides.length} medio{heroSlides.length !== 1 ? 's' : ''}
                                    </p>
                                  </div>
                                  <div className="AD-hero-field">
                                    <strong className="AD-hero-label">Texto del botón:</strong>
                                    <p className="AD-hero-value">{hero.textbuttom || 'N/A'}</p>
                                  </div>
                                  <div className="AD-hero-field">
                                    <strong className="AD-hero-label">URL del botón:</strong>
                                    <p className="AD-hero-url">{hero.urlbuttom || 'N/A'}</p>
                                  </div>
                                </div>
                                <div className="AD-hero-actions">
                                  <button
                                    className="AD-hero-btn AD-hero-btn-edit"
                                    onClick={() => openEditModal(hero)}
                                    disabled={heroesLoading}
                                  >
                                    <i className="bi-pencil" />
                                    Editar
                                  </button>
                                  <button
                                    className="AD-hero-btn AD-hero-btn-delete"
                                    onClick={() => handleDeleteHero(hero.id)}
                                    disabled={heroesLoading}
                                  >
                                    <i className="bi-trash" />
                                    Eliminar
                                  </button>
                                </div>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Modal para crear/editar Hero */}
            {showHeroModal && (
              <div className="AD-hero-modal-overlay" onClick={closeHeroModal}>
                <div className="AD-hero-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="AD-hero-modal-header">
                    <h4 className="AD-hero-modal-title">{editingHero ? 'Editar Hero' : 'Crear Hero'}</h4>
                    <button className="AD-hero-modal-close" onClick={closeHeroModal}>
                      <i className="bi-x-lg" />
                    </button>
                  </div>
                  <div className="AD-hero-modal-body">
                    <div className="AD-hero-form-group">
                      <label className="AD-hero-form-label">Secuencia del hero</label>
                      <span className="AD-hero-form-help d-block mb-2">
                        Orden de reproducción en la landing: los videos hasta que terminen; cada imagen se muestra 15 segundos. Podés mezclar tipos.
                      </span>
                      <div className="AD-hero-slides-list">
                        {heroSlideRows.map((row, idx) => (
                          <div key={row.key} className="AD-hero-slide-row">
                            <div className="AD-hero-slide-row-header">
                              <span className="AD-hero-slide-row-title">Slide {idx + 1}</span>
                              <div className="AD-hero-slide-row-tools">
                                <button
                                  type="button"
                                  className="AD-hero-slide-icon-btn"
                                  onClick={() => moveHeroSlide(idx, -1)}
                                  disabled={heroesLoading || idx === 0}
                                  title="Mover arriba"
                                >
                                  <i className="bi bi-arrow-up" />
                                </button>
                                <button
                                  type="button"
                                  className="AD-hero-slide-icon-btn"
                                  onClick={() => moveHeroSlide(idx, 1)}
                                  disabled={heroesLoading || idx >= heroSlideRows.length - 1}
                                  title="Mover abajo"
                                >
                                  <i className="bi bi-arrow-down" />
                                </button>
                                <button
                                  type="button"
                                  className="AD-hero-slide-icon-btn AD-hero-slide-icon-btn--danger"
                                  onClick={() => handleHeroSlideRemove(idx)}
                                  disabled={heroesLoading || heroSlideRows.length <= 1}
                                  title="Quitar slide"
                                >
                                  <i className="bi bi-trash" />
                                </button>
                              </div>
                            </div>
                            <Form.Select
                              value={row.type}
                              onChange={(e) =>
                                handleHeroSlideTypeChange(idx, e.target.value === 'video' ? 'video' : 'image')
                              }
                              className="AD-hero-form-input mb-2"
                              disabled={heroesLoading}
                            >
                              <option value="image">Imagen</option>
                              <option value="video">Video</option>
                            </Form.Select>
                            <label className="AD-hero-form-label small text-muted mb-1">Archivo</label>
                            <input
                              type="file"
                              accept={row.type === 'video' ? 'video/*' : 'image/*'}
                              onChange={(e) => handleHeroSlideFileChange(idx, e)}
                              disabled={heroesLoading || uploadingImage || uploadingVideo}
                              className="AD-hero-form-input"
                            />
                            <span className="AD-hero-form-help">
                              {row.type === 'video'
                                ? 'MP4, WebM, etc. (mismo límite que antes en el servidor / proxy).'
                                : 'JPEG, PNG, GIF, WebP.'}
                            </span>
                            {row.url && !row.file && (
                              <p className="AD-hero-slide-saved-url small text-muted mt-1 mb-0">
                                Guardado: {row.url}
                              </p>
                            )}
                            {(row.preview || row.url) && (
                              <div className="AD-hero-preview-wrapper mt-2">
                                {row.type === 'video' ? (
                                  <video
                                    src={row.preview || resolveImageUrl(row.url)}
                                    controls
                                    className="AD-hero-preview-image"
                                    style={{ maxWidth: '100%', maxHeight: '220px' }}
                                  >
                                    Tu navegador no soporta videos HTML5.
                                  </video>
                                ) : (
                                  <img
                                    src={row.preview || resolveImageUrl(row.url)}
                                    alt={`Preview slide ${idx + 1}`}
                                    className="AD-hero-preview-image"
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="AD-heroes-create-btn mt-2"
                        onClick={addHeroSlideRow}
                        disabled={heroesLoading}
                      >
                        <i className="bi bi-plus-lg" />
                        Agregar slide
                      </button>
                      {(uploadingImage || uploadingVideo) && (
                        <div className="AD-hero-uploading mt-2">
                          <Spinner size="sm" animation="border" />
                          <span>Subiendo archivos…</span>
                        </div>
                      )}
                    </div>
                    <div className="AD-hero-form-group">
                      <label className="AD-hero-form-label">Texto del Botón *</label>
                      <input
                        type="text"
                        placeholder="Ej: Comprar ahora"
                        value={heroForm.textbuttom}
                        onChange={(e) => setHeroForm({ ...heroForm, textbuttom: e.target.value })}
                        required
                        className="AD-hero-form-input"
                      />
                    </div>
                    <div className="AD-hero-form-group">
                      <label className="AD-hero-form-label">URL del Botón *</label>
                      <input
                        type="text"
                        placeholder="https://ejemplo.com o /ruta"
                        value={heroForm.urlbuttom}
                        onChange={(e) => setHeroForm({ ...heroForm, urlbuttom: e.target.value })}
                        required
                        className="AD-hero-form-input"
                      />
                    </div>
                    <div className="AD-hero-modal-actions">
                      <button className="AD-hero-modal-btn AD-hero-modal-btn-cancel" onClick={closeHeroModal}>
                        Cancelar
                      </button>
                      <button
                        className="AD-hero-modal-btn AD-hero-modal-btn-submit"
                        onClick={editingHero ? handleUpdateHero : handleCreateHero}
                        disabled={heroesLoading}
                      >
                        {heroesLoading ? (
                          <>
                            <Spinner size="sm" animation="border" />
                            <span>{editingHero ? 'Actualizando...' : 'Creando...'}</span>
                          </>
                        ) : (
                          editingHero ? 'Actualizar' : 'Crear'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

const AdminDashboard = () => (
  <AdminNotificationsProvider>
    <AdminDashboardInner />
  </AdminNotificationsProvider>
);

export default AdminDashboard;

